import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { fetchProjectAsset } from "components/SmartSurveyor/SmartSurveyorV3/utils/signedAssetUrls";
import { findCableGaps } from "../findCableGaps";
import type { CableViewBundle, SldCircuits } from "../findCableGaps";
import type { UseCableGaps } from "./types";

const projectBase = (projectId: string): string =>
  `smart-surveyor/projects/${projectId}/agent_output/cables`;

const viewBundleKey = (
  projectId: string,
  annotationKey: string,
): string | null => {
  const anndir = annotationKey
    .split("/annotations/")[1]
    ?.split("/counts.json")[0];
  if (!anndir) return null;
  return `${projectBase(projectId)}/view/${anndir}.json`;
};

const fetchJson = async <T>(
  projectId: string,
  key: string,
): Promise<T | null> => {
  try {
    const res = await fetchProjectAsset({ projectId, key });
    if (!res.ok) return null;
    return (await res.json()) as T;
  } catch {
    return null;
  }
};

interface LoadedArtifacts {
  bundles: CableViewBundle[];
  sldCircuits: SldCircuits | null;
}

export const useCableGaps: UseCableGaps = ({ projectId, plans }) => {
  const { t } = useTranslation();
  const [loaded, setLoaded] = useState<LoadedArtifacts | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  // Plans arrive as a fresh array each render; key off their identity so the
  // fetch runs when the takeoff changes, not on every parent render.
  const planKey = plans.map((plan) => plan.annotationKey).join("|");

  // Worded on read, not on fetch: switching the app to German must re-say the
  // gaps in German without going back to S3 for artifacts that never changed.
  const gaps = useMemo(
    () =>
      loaded?.bundles.length
        ? findCableGaps({
            bundles: loaded.bundles,
            sldCircuits: loaded.sldCircuits,
            t,
          })
        : [],
    [loaded, t],
  );

  useEffect(() => {
    if (!projectId || !planKey) {
      setLoaded(null);
      return;
    }

    let cancelled = false;
    const load = async (): Promise<void> => {
      setIsLoading(true);
      const [bundles, sldCircuits] = await Promise.all([
        Promise.all(
          planKey
            .split("|")
            .map((annotationKey) => viewBundleKey(projectId, annotationKey))
            .map((key) =>
              key
                ? fetchJson<CableViewBundle>(projectId, key)
                : Promise.resolve(null),
            ),
        ),
        fetchJson<SldCircuits>(
          projectId,
          `${projectBase(projectId)}/sld_circuits.json`,
        ),
      ]);
      if (cancelled) return;
      setLoaded({
        bundles: bundles.filter((bundle): bundle is CableViewBundle =>
          Boolean(bundle),
        ),
        sldCircuits,
      });
      setIsLoading(false);
    };

    void load();
    return () => {
      cancelled = true;
    };
  }, [projectId, planKey]);

  return {
    gaps,
    isLoading,
    bundles: loaded?.bundles ?? [],
    sldCircuits: loaded?.sldCircuits ?? null,
  };
};
